import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Trash2 } from "lucide-react";
import AllowancedetailModal from "../../../modals/Hrms/AllowancedetailModal";
import ApprovalAllowanceModal from "../../../modals/Hrms/ApprovalAllowanceModal";
import { fetchAllowances, deleteAllowance, resetDeleteStatus } from "../../../redux/allowanceSlice";

const AllowanceDetail = () => {
  const dispatch = useDispatch();
  const { allowances, status, error, deleteStatus } = useSelector((state) => state.allowances);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState(null);
  const [notification, setNotification] = useState(null);

  // Fetch allowances on component mount
  useEffect(() => {
    dispatch(fetchAllowances());
  }, [dispatch]);

  // Handle notifications for API actions
  useEffect(() => {
    if (deleteStatus === "succeeded") {
      setNotification({ type: "success", message: "Allowance deleted successfully" });
      setTimeout(() => {
        dispatch(resetDeleteStatus());
        setNotification(null);
      }, 3000);
    } else if (deleteStatus === "failed") {
      setNotification({ type: "error", message: error || "Failed to delete allowance" });
      setTimeout(() => {
        dispatch(resetDeleteStatus());
        setNotification(null);
      }, 3000);
    } else if (status === "failed") {
      setNotification({ type: "error", message: error || "Failed to fetch allowances" });
      setTimeout(() => setNotification(null), 3000);
    }
  }, [status, deleteStatus, error, dispatch]);

  const handleEditAllowance = (allowance) => {
    setEditingAllowance(allowance);
    setIsApplyModalOpen(true);
  };

  const handleDeleteAllowance = (id) => {
    dispatch(deleteAllowance(id));
  };

  const handleOpenApprovalModal = (allowance) => {
    setEditingAllowance(allowance);
    setIsApprovalModalOpen(true);
  };

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Session Name: March-2024</h1>
        <button
          onClick={() => {
            setEditingAllowance(null);
            setIsApplyModalOpen(true);
          }}
          className="bg-[#241763] text-white px-4 py-2 rounded"
        >
          Apply Allowance
        </button>
      </div>

      {notification && (
        <div
          className={`mb-4 p-2 rounded ${
            notification.type === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`}
        >
          {notification.message}
        </div>
      )}

      {status === "loading" && <p>Loading allowances...</p>}
      {status === "succeeded" && (
        <div className="overflow-x-auto overflow-visible">
          <div className="max-h-[70vh] overflow-x-auto overflow-y-auto">
            <table className="table-auto w-full mt-6 text-sm">
              <thead className="py-4 border-b font-bold">
                <tr>
                  <th>EMP. CODE</th>
                  <th>EMPLOYEE NAME</th>
                  <th>APPLIED DATE</th>
                  <th>SALARY</th>
                  <th>START SESSION</th>
                  <th>ALLOWANCE TYPE</th>
                  <th>AMOUNT</th>
                  <th>DESCRIPTION</th>
                  <th>STATUS</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {allowances.map((allowance) => (
                  <tr key={allowance.id}>
                    <td>{allowance.employeeCode || "SAS-015"}</td>
                    <td>
                      <div>
                        <div className="font-medium">{allowance.employeeName}</div>
                        <div className="text-sm text-gray-500">
                          {allowance.department || "Engineering & Production"}
                        </div>
                      </div>
                    </td>
                    <td>{new Date(allowance.applyDate).toLocaleDateString()}</td>
                    <td>{allowance.salary}</td>
                    <td>{allowance.sessionName}</td>
                    <td>{allowance.duesType}</td>
                    <td>{allowance.duesAmount}</td>
                    <td>{allowance.description || "-"}</td>
                    <td
                      className="cursor-pointer"
                      onClick={() => handleOpenApprovalModal(allowance)}
                    >
                      <span
                        className={`px-2 py-1 rounded ${
                          allowance.status === "Pending"
                            ? "bg-orange-100 text-orange-600"
                            : "bg-green-100 text-green-600"
                        }`}
                      >
                        {allowance.status}
                      </span>
                    </td>
                    <td className="space-x-2 flex items-center py-2">
                      <button
                        className="bg-[#B6A5C9]/50 text-white p-2 rounded mr-2 hover:bg-[#241763]/60 group"
                        onClick={() => handleEditAllowance(allowance)}
                      >
                        <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                      </button>
                      <button
                        className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                        onClick={() => handleDeleteAllowance(allowance.id)}
                        disabled={deleteStatus === "loading"}
                      >
                        <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AllowancedetailModal
        isOpen={isApplyModalOpen}
        onClose={() => {
          setEditingAllowance(null);
          setIsApplyModalOpen(false);
        }}
        initialData={editingAllowance}
      />
      <ApprovalAllowanceModal
        isOpen={isApprovalModalOpen}
        onClose={() => {
          setEditingAllowance(null);
          setIsApprovalModalOpen(false);
        }}
        allowance={editingAllowance}
      />
    </div>
  );
};

export default AllowanceDetail;